import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';
import { RxGraphQLReplicationState } from 'rxdb/plugins/replication-graphql';

const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';

export const refreshTokenOnSyncState = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  replicationState: RxGraphQLReplicationState<any>,
  maxTimeToExpiry = 0, // seconds
) => {
  let tokenExpired = false;
  let tokenExpiringSoon = false;
  let currentIdTokenExpiration = -1;
  const currentTimestamp = dayjs().unix();
  const collectionName = replicationState.collection.name;

  try {
    // 1. Get the id token expiration on the token in storage
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const currentSession = await Auth.currentSession();
    const currentIdToken = currentSession.getIdToken().getJwtToken();
    currentIdTokenExpiration = currentSession.getIdToken().getExpiration(); // seconds

    const diff = currentIdTokenExpiration - currentTimestamp; // if this is positive its still a valid token
    // eslint-disable-next-line no-console
    if (DEBUG_RXDB) console.log(`Token will expire in ${diff} seconds`);

    tokenExpired = diff <= 0;
    tokenExpiringSoon = diff <= maxTimeToExpiry;

    if (!tokenExpiringSoon && DEBUG_RXDB)
      console.log(
        `Token newer than max time to expire... no need to refresh (${diff}s > ${maxTimeToExpiry}s)`,
      );

    // 2. If the id token is expired, refresh the token
    if (tokenExpired || tokenExpiringSoon) {
      const refreshToken = currentSession.getRefreshToken();
      if (DEBUG_RXDB) console.log(`Refresh token: ${refreshToken}`);

      cognitoUser.refreshSession(refreshToken, (err: unknown, session: unknown) => {
        if (err) {
          if (DEBUG_RXDB) console.log(`Token refresh failed:`, err);

          if (
            err.message === 'Request timed out.' ||
            err.message?.toLowerCase().includes('network') ||
            err.message?.toLowerCase().includes('offline')
          ) {
            return;
          }

          Sentry.captureException(err, {
            tags: {
              replicationError: true,
              rxDBError: true,
              collectionName,
              tokenExpired,
              tokenExpiringSoon,
              refreshTokenError: true,
              currentIdTokenExpiration,
              currentTimestamp,
            },
          });
          return;
        }

        const newIdToken = session.idToken.getJwtToken();
        if (DEBUG_RXDB) console.log(`New ID token: ${newIdToken}`);

        replicationState.setHeaders({ Authorization: `Bearer ${newIdToken}` });
      });
    } else {
      // 3. Token not expired but set the token on the collection headers regardless just in case the collection has a stale token
      replicationState.setHeaders({ Authorization: `Bearer ${currentIdToken}` });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error refreshing token on collection:', replicationState.collection.name, error);

    if (
      error.message === 'Request timed out.' ||
      error.message?.toLowerCase().includes('network') ||
      error.message?.toLowerCase().includes('offline')
    ) {
      return;
    }
    Sentry.captureException(error, {
      tags: {
        replicationError: true,
        rxDBError: true,
        collectionName,
        tokenExpired,
        tokenExpiringSoon,
        refreshTokenError: true,
        currentIdTokenExpiration,
        currentTimestamp,
      },
    });
  }
};
